<script setup>
import CIconToggle from '@convenia/components/CIconToggle/CIconToggle.vue'
import CTypo from '@convenia/components/CTypo'
import { computed, defineProps, defineEmits, ref, watch } from 'vue'
import useMediaQuery from '@convenia/composables/useMediaQuery'

const LINE_BREAK_THRESHOLD = 9
const MAX_PER_ROW = 6

const props = defineProps({
  rating: {
    type: String,
    default: 'star',
    validator: value => [ 'radio', 'heart', 'star' ].includes(value)
  },
  range: {
    type: Array,
    default: () => [],
    validator: (value) => {
      return Array.isArray(value) && value.length === 2
    }
  },
  modelValue: {
    type: Number,
    default: 0
  },
  isDisabled: {
    type: Boolean,
    default: false
  },
  gap: {
    type: String,
    default: '20px'
  },
  size: {
    type: Number,
    default: 24
  },
  showValues: {
    type: Boolean,
    default: false
  },
  fieldName: {
    type: String,
    default: ''
  }
})

const emits = defineEmits([ 'update:modelValue', 'setRating' ])

const { isMobile } = useMediaQuery()

const hoveredIndex = ref(-1)
const currentRating = ref(props.modelValue !== 0 ? props.modelValue : null)

const items = computed(() => {
  const { range } = props
  if (!Array.isArray(range) || range.length < 2) return []

  const [ min, max ] = range
  const itemsArray = []
  for (let i = min; i <= max; i++) {
    itemsArray.push(i)
  }
  return itemsArray
})

const shouldBreakLine = computed(() => {
  return isMobile.value && items.value.length >= LINE_BREAK_THRESHOLD
})

const rows = computed(() => {
  if (!shouldBreakLine.value) return [ items.value ]

  const total = items.value.length
  const perRow = Math.min(MAX_PER_ROW, Math.ceil(total / 2))
  const extra = total % perRow

  const rows = extra
    ? [ items.value.slice(0, extra) ]
    : []

  for (let i = extra; i < total; i += perRow) {
    rows.push(items.value.slice(i, i + perRow))
  }

  return rows
})

const containerClasses = computed(() => [
  'rating-container',
  { disabled: props.isDisabled },
  props.rating,
  { 'break-lines': shouldBreakLine.value }
])

const containerStyles = computed(() => ({
  gap: props.gap
}))

const handleClick = (itemIndex) => {
  if (props.isDisabled) return

  let newRating = itemIndex
  if (currentRating.value === itemIndex) return

  if (itemIndex < currentRating.value) {
    newRating = itemIndex
  }

  if (itemIndex > currentRating.value) {
    newRating = itemIndex
  }

  currentRating.value = newRating
  emits('update:modelValue', newRating)
  emits('setRating', newRating)
}

const handleMouseEnter = (itemIndex) => {
  if (props.isDisabled) return
  hoveredIndex.value = itemIndex
}

const handleMouseLeave = () => {
  if (props.isDisabled) return
  hoveredIndex.value = -1
}

const isItemSelected = (itemIndex) => {
  if (hoveredIndex.value !== -1) {
    if (hoveredIndex.value < currentRating.value) {
      return itemIndex <= hoveredIndex.value
    }
  }
  return currentRating.value !== null && itemIndex <= currentRating.value
}

watch(() => props.modelValue, (newValue) => {
  currentRating.value = newValue === 0 && currentRating.value === null ? null : newValue
}, { immediate: true })

</script>

<template>
  <div :class="containerClasses">
    <div
      v-for="(row, rowIndex) in rows"
      :key="rowIndex"
      :style="containerStyles"
      class="rating-row"
    >
      <div
        v-for="item in row"
        :key="item"
        @mouseenter="handleMouseEnter(item)"
        @mouseleave="handleMouseLeave"
        class="rating-item-wrapper"
      >
        <c-icon-toggle
          :size="props.size"
          :disabled="props.isDisabled"
          :type="props.rating"
          :hovered="hoveredIndex !== -1 && (item <= hoveredIndex || item <= currentRating)"
          :checked="isItemSelected(item)"
          :data-testid="`rating-item-${props.fieldName}-${item}`"
          @update:checked="handleClick(item)"
        />

        <span
          v-if="showValues"
          class="rating-value"
          :class="{ active: item <= currentRating }"
        >
          <c-typo
            class="rating-value-text"
            type="body-1"
            opacity="base-80"
            weight="medium"
            color="text"
          >
            {{ item }}
          </c-typo>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.rating-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;

  &.break-lines {
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
  }

  & > .rating-row {
    display: flex;
    justify-content: center;

    & > .rating-item-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }
  }
}
</style>
